import type { Router, RouteRecordNormalized, RouteLocationNormalizedLoaded } from 'vue-router'
import { usePermissionStore } from '@/pinia/stores/permission'

const GENERATION_RECORDS_PATH_HINTS = ['/generation-records', '/generation', 'generation-records', 'generation']
const GENERATION_RECORDS_NAME_HINTS = [
  'generation-records',
  'generation',
  'GenerationRecords',
  'Generation',
  '生成记录',
]

const GENERATION_RECORDS_TITLES = new Set([
  '生成记录',
  'Generation Records',
  'Registros de Geração',
])

function normalizeMenuPath(path: string): string {
  if (!path) return ''
  return path.startsWith('/') ? path : `/${path}`
}

/** 是否为「生成记录」页路径（兼容 /generation、/xxx/generation-records 等动态菜单） */
export function isGenerationRecordsPagePath(path: string): boolean {
  const normalized = path.replace(/\/+$/, '').toLowerCase()
  if (!normalized) return false
  if (normalized.includes('batch-generation') || normalized.includes('video-generation')) {
    return false
  }
  if (normalized === '/generation' || normalized === '/generation-records') return true
  if (normalized.endsWith('/generation-records') || normalized.endsWith('/generation')) {
    return true
  }
  return false
}

function isGenerationRecordsNameOrTitle(nameOrTitle: string): boolean {
  if (!nameOrTitle) return false
  if (GENERATION_RECORDS_TITLES.has(nameOrTitle)) return true
  const lower = nameOrTitle.toLowerCase()
  return (
    lower === 'generation-records' ||
    lower === 'generation' ||
    lower === 'generationrecords'
  )
}

function isGenerationRecordsRoute(route: RouteRecordNormalized): boolean {
  const path = route.path.replace(/\/+$/, '').toLowerCase()
  if (!path) return false
  if (path.includes('batch-generation') || path.includes('video-generation')) return false

  if (isGenerationRecordsPagePath(path)) return true
  if (isGenerationRecordsNameOrTitle(String(route.name ?? ''))) return true
  if (isGenerationRecordsNameOrTitle(String(route.meta?.title ?? ''))) return true

  return false
}

/** 当前路由是否为生成记录页（path / name / title 任一命中） */
export function isCurrentGenerationRecordsRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'path' | 'name' | 'meta'>,
): boolean {
  if (isGenerationRecordsPagePath(route.path)) return true
  if (isGenerationRecordsNameOrTitle(String(route.name ?? ''))) return true
  if (isGenerationRecordsNameOrTitle(String(route.meta?.title ?? ''))) return true
  return false
}

function findMenuPathFromTree(menus: unknown[]): string | null {
  if (!Array.isArray(menus)) return null
  for (const raw of menus) {
    const menu = raw as { path?: string; name?: string; component?: string; children?: unknown[] }
    const component = String(menu.component ?? '')
    const name = String(menu.name ?? '')
    if (
      component.includes('generation/index') ||
      component.includes('/generation/index') ||
      name === 'generation-records' ||
      name === '生成记录' ||
      name === 'GenerationRecords'
    ) {
      return menu.path ? normalizeMenuPath(menu.path) : null
    }
    if (menu.children?.length) {
      const nested = findMenuPathFromTree(menu.children)
      if (nested) return nested
    }
  }
  return null
}

/** 解析「生成记录」页路由（兼容动态菜单注册的路径） */
export function resolveGenerationRecordsRoute(router: Router): { path: string } | { name: string } {
  const registered = router.getRoutes().filter(isGenerationRecordsRoute)
  if (registered.length > 0) {
    const route = registered[0]
    if (route.name) return { name: String(route.name) }
    return { path: route.path.startsWith('/') ? route.path : `/${route.path}` }
  }

  try {
    const menuPath = findMenuPathFromTree(usePermissionStore().menuTree as unknown[])
    if (menuPath) {
      const resolved = router.resolve({ path: menuPath })
      if (resolved.matched.length > 0) return { path: menuPath }
    }
  } catch {
    // ignore store unavailable
  }

  for (const name of GENERATION_RECORDS_NAME_HINTS) {
    const resolved = router.resolve({ name })
    if (resolved.matched.length > 0 && isGenerationRecordsRoute(resolved.matched[resolved.matched.length - 1])) {
      return { name }
    }
  }

  for (const path of GENERATION_RECORDS_PATH_HINTS) {
    const fullPath = normalizeMenuPath(path)
    const resolved = router.resolve({ path: fullPath })
    if (resolved.matched.length > 0 && isGenerationRecordsRoute(resolved.matched[resolved.matched.length - 1])) {
      return { path: fullPath }
    }
  }

  return { path: '/generation-records' }
}

/** 当前路由表能否打开生成记录（避免把兜底 path 当成已注册） */
export function canOpenGenerationRecords(router: Router): boolean {
  try {
    const resolved = router.resolve(resolveGenerationRecordsRoute(router))
    const last = resolved.matched[resolved.matched.length - 1]
    return Boolean(last && isGenerationRecordsRoute(last))
  } catch {
    return false
  }
}
